import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  listPenaltyTypes,
  createPenaltyType,
  createPenaltyEntry,
  approvePenaltyEntry,
  reversePenaltyEntry,
  getPenaltiesWorkspace,
} from '@/api/services/penalties.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  CreatePenaltyTypeDto,
  CreatePenaltyEntryDto,
  MyPenaltyListQuery,
} from '@/features/penalties/types/penalty.types'

export const penaltyKeys = {
  all: (companyId: string | null) => ['penalties', companyId] as const,
  types: (companyId: string | null) => ['penalties', companyId, 'types'] as const,
  workspace: (params: MyPenaltyListQuery | undefined) =>
    ['penalties', 'workspace', params] as const,
}

export function usePenaltiesWorkspace(
  params: MyPenaltyListQuery | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: penaltyKeys.workspace(params),
    queryFn: () => getPenaltiesWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function usePenaltyTypes(companyId: string | null) {
  return useQuery({
    queryKey: penaltyKeys.types(companyId),
    queryFn: () => listPenaltyTypes(companyId!),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!companyId,
  })
}

export function useCreatePenaltyType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreatePenaltyTypeDto }) =>
      createPenaltyType(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: penaltyKeys.types(variables.companyId) })
      toast.success('Penalty type created.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCreatePenaltyEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreatePenaltyEntryDto }) =>
      createPenaltyEntry(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: penaltyKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['penalties', 'workspace'] })
      // Penalties are payroll deductions — keep run previews fresh.
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Penalty issued.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useApprovePenaltyEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, entryId }: { companyId: string; entryId: string }) =>
      approvePenaltyEntry(companyId, entryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: penaltyKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['penalties', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Penalty approved.')
    },
    onError: (error, variables) => {
      toast.error(getErrorMessage(error))
      // The entry may have been decided elsewhere — refetch so stale rows clear.
      queryClient.invalidateQueries({ queryKey: penaltyKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['penalties', 'workspace'] })
    },
  })
}

export function useReversePenaltyEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, entryId }: { companyId: string; entryId: string }) =>
      reversePenaltyEntry(companyId, entryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: penaltyKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: ['penalties', 'workspace'] })
      queryClient.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Penalty reversed.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
